import { BrowserRouter as Router, Route, Routes, Navigate } from "react-router-dom";
import { Box, CircularProgress } from "@mui/material";
import { ROUTES } from "./common/constant/routes";
import Login from "./pages/Login/Login";
import Overview from "./pages/Overview";
import Sales from "./pages/Sales/Sales";
import Withdrawals from "./pages/Withdrawals/Withdrawals";
import UsersAndSellers from "./pages/UsersAndSellers";
import ChatMonitor from "./pages/ChatMonitor/ChatMonitor";
import Platform from "./pages/Platform";
import Activity from "./pages/Activity";
import SearchAcronyms from "./pages/SearchAcronyms";
import Emails from "./pages/Emails";
import Promotions from "./pages/Promotions";
import AppLayout from "./components/Layout";
import ProtectedRoute from "./components/ProtectedRoute";
import { useAuth } from "./common/auth";

/** "/" goes to Overview, which adapts to what the admin can see. Accounts
 * with an admin row but no pages at all are sent back to login. */
function LandingRedirect() {
  const { adminUser, loading, firebaseUser } = useAuth();

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    );
  }

  if (!firebaseUser || !adminUser) {
    return <Navigate to={ROUTES.LOGIN} replace />;
  }

  if (adminUser.role !== "superadmin" && (adminUser.permissions ?? []).length === 0) {
    return (
      <Navigate to={ROUTES.LOGIN} replace state={{ error: "Your account does not have access to this dashboard." }} />
    );
  }

  return <Navigate to={ROUTES.OVERVIEW} replace />;
}

function App() {
  return (
    <Router>
      <Routes>
        {/* Public route */}
        <Route path={ROUTES.LOGIN} element={<Login />} />

        {/* Protected routes within the app layout */}
        <Route
          element={
            <ProtectedRoute>
              <AppLayout />
            </ProtectedRoute>
          }
        >
          <Route path={ROUTES.LANDING} element={<LandingRedirect />} />
          <Route path={ROUTES.OVERVIEW} element={<Overview />} />

          {/* Operations */}
          <Route
            path={ROUTES.SALES}
            element={
              <ProtectedRoute requiredPermission="sales">
                <Sales />
              </ProtectedRoute>
            }
          />
          <Route
            path={ROUTES.WITHDRAWALS}
            element={
              <ProtectedRoute requiredPermission="withdrawals">
                <Withdrawals />
              </ProtectedRoute>
            }
          />
          <Route
            path={ROUTES.CHAT_MONITOR}
            element={
              <ProtectedRoute superadminOnly>
                <ChatMonitor />
              </ProtectedRoute>
            }
          />

          {/* People: users, seller applications and messages are tabs of one page */}
          <Route
            path={ROUTES.USER_MANAGEMENT}
            element={
              <ProtectedRoute requiredPermission={["registered_users", "seller_creation", "notifications"]}>
                <UsersAndSellers />
              </ProtectedRoute>
            }
          />

          {/* Marketing */}
          <Route
            path={ROUTES.PROMOTIONS}
            element={
              <ProtectedRoute superadminOnly>
                <Promotions />
              </ProtectedRoute>
            }
          />

          {/* Platform: health and traffic */}
          <Route
            path={ROUTES.PLATFORM}
            element={
              <ProtectedRoute superadminOnly>
                <Platform />
              </ProtectedRoute>
            }
          />

          <Route
            path={ROUTES.ACTIVITY}
            element={
              <ProtectedRoute superadminOnly>
                <Activity />
              </ProtectedRoute>
            }
          />
          <Route
            path={ROUTES.SEARCH_ACRONYMS}
            element={
              <ProtectedRoute superadminOnly>
                <SearchAcronyms />
              </ProtectedRoute>
            }
          />
          <Route
            path={ROUTES.EMAILS}
            element={
              <ProtectedRoute superadminOnly>
                <Emails />
              </ProtectedRoute>
            }
          />

          {/* Old pages, kept so bookmarks and shared links still work */}
          <Route path={ROUTES.SELLER_CREATION} element={<Navigate to={`${ROUTES.USER_MANAGEMENT}?tab=applications`} replace />} />
          <Route path={ROUTES.NOTIFICATIONS} element={<Navigate to={`${ROUTES.USER_MANAGEMENT}?tab=messages`} replace />} />
          <Route path={ROUTES.REGISTERED_USERS} element={<Navigate to={ROUTES.USER_MANAGEMENT} replace />} />
          <Route path={ROUTES.MANUAL_ORDER} element={<Navigate to={`${ROUTES.SALES}?create=1`} replace />} />
          <Route path={ROUTES.APP_HEALTH} element={<Navigate to={ROUTES.PLATFORM} replace />} />
          <Route path={ROUTES.TRAFFIC_ANALYTICS} element={<Navigate to={ROUTES.PLATFORM} replace />} />
        </Route>

        {/* Catch-all */}
        <Route path="*" element={<Navigate to={ROUTES.LANDING} replace />} />
      </Routes>
    </Router>
  );
}

export default App;
